import React from 'react';
import { ButtonPrimary } from '../../../components/auth/ButtonPrimary';
import { OTPInput } from '../../../components/auth/OTPInput';
import { Timer } from '../../../components/auth/Timer';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../../components/ui/button';

interface OTPStepProps {
    email: string;
    otp: string[];
    timer: number;
    devOtp?: string | null;
    onChange: (otp: string[]) => void;
    onComplete: (code: string) => void;
    onSubmit: () => Promise<void>;
    onResend: () => Promise<void>;
    onBack: () => void;
    isLoading: boolean;
    isResending?: boolean;
    error: string | null;
}

export const OTPStep: React.FC<OTPStepProps> = ({
    email,
    otp,
    timer,
    devOtp,
    onChange,
    onComplete,
    onSubmit,
    onResend,
    onBack,
    isLoading,
    isResending = false,
    error
}) => {
    // Timer color logic: < 3 mins (180s) red, < 7 mins (420s) yellow
    const getTimerColor = () => {
        if (timer < 180) return 'text-error';
        if (timer < 420) return 'text-yellow-500';
        return 'text-text-secondary';
    };

    return (
        <div className="flex flex-col gap-6 w-full max-w-sm animate-fade-in text-center">
            {/* H20: botón Volver en flujo normal — evita overlap con heading en mobile */}
            <div className="flex justify-start">
                <button
                    onClick={onBack}
                    className="text-text-muted hover:text-text-primary transition-colors flex items-center gap-2 group"
                >
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-sm font-medium">Volver</span>
                </button>
            </div>

            <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tight text-text-primary">
                    Código de acceso
                </h1>
                {/* El backend contesta lo mismo esté o no el mail en la lista: no se afirma que se mandó.
                    Y el código sólo anda en el navegador que lo pidió (cookie del desafío). */}
                <p className="text-text-secondary">
                    Si <span className="font-semibold text-text-primary">{email}</span> tiene acceso, te llega un
                    código de 6 dígitos. Puede tardar un minuto: revisá también el spam. Escribilo en esta misma
                    ventana.
                </p>
                <button
                    onClick={onBack}
                    className="text-sm text-primary hover:underline font-medium"
                >
                    ¿Email incorrecto?
                </button>
            </div>

            {/* DEMO MODE: pre-show OTP para que el flujo se pruebe sin
                infraestructura de email. Copy en clave "demo", no "dev",
                para que la presencia del cuadro no canta a entorno sucio
                cuando un cliente vea el flujo. */}
            {devOtp && (
                <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 text-center">
                    <p className="text-xs font-semibold text-violet-700 uppercase tracking-wider mb-1 flex items-center justify-center gap-1.5">
                        ✨ Modo demo · usá este código
                    </p>
                    <p className="text-2xl font-mono font-bold tracking-[0.3em] text-violet-900 select-all">
                        {devOtp}
                    </p>
                </div>
            )}

            <div className="space-y-6">
                <div>
                    <OTPInput
                        value={otp}
                        onChange={onChange}
                        onComplete={onComplete}
                        error={!!error}
                        disabled={isLoading}
                    />
                    {error && (
                        <p className="mt-4 text-sm text-error font-medium animate-shake">
                            {error}
                        </p>
                    )}
                </div>

                <ButtonPrimary
                    onClick={onSubmit}
                    isLoading={isLoading}
                    disabled={otp.some(d => !d)} // Disable if incomplete
                >
                    Verificar acceso
                </ButtonPrimary>

                <div className="flex flex-col items-center gap-4">
                    <div className={getTimerColor()}>
                        <Timer seconds={timer} />
                    </div>

                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onResend}
                        disabled={timer > 570 || isLoading || isResending} // Enable after 30s
                        className={timer > 570 ? 'opacity-50' : 'text-primary hover:text-primary-hover hover:bg-primary/5'}
                    >
                        {isResending ? 'Enviando...' : 'Reenviar código'}
                    </Button>

                    <p className="text-xs text-text-muted">
                        ¿No te llega? Pedile acceso a un dueño de Accesos del equipo de Deenex.
                    </p>
                </div>
            </div>
        </div>
    );
};
